// Browser Notification Service
class NotificationService {
  constructor() {
    this.permission = Notification?.permission || 'default';
    this.enabled = false;
  }

  // Play notification sound
  playNotificationSound() {
    try {
      // Create audio element with data URI for notification sound
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSp+z+/ikTYGHGa86M2QUAoUYLns7KFNDAhXqO7qpmkgBDF+yOzmlEwOF0Wo7OupWBQJT6Xr6KddGAUvgM/u4pI5ByBxyf');
      audio.volume = 0.5;
    } catch (error) {
    }
  }

  // Check if browser supports notifications
  isSupported() {
    return 'Notification' in window;
  }

  // Get current permission status
  getPermissionStatus() {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  // Request permission from user
  async requestPermission() {
    if (!this.isSupported()) {
      return 'unsupported';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      this.enabled = permission === 'granted';
      
      // Store permission preference
      localStorage.setItem('notification_permission', permission);
      localStorage.setItem('notification_permission_timestamp', Date.now().toString());
      
      return permission;
    } catch (error) {
      return 'denied';
    }
  }

  // Check if we should ask for permission (not too frequently)
  shouldAskPermission() {
    const lastAsked = localStorage.getItem('notification_permission_timestamp');
    const permission = localStorage.getItem('notification_permission');
    
    // Don't ask if already granted or if browser doesn't support
    if (!this.isSupported() || permission === 'granted') {
      return false;
    }
    
    // Don't ask if denied recently (within 7 days)
    if (permission === 'denied' && lastAsked) {
      const daysSinceAsked = (Date.now() - parseInt(lastAsked)) / (1000 * 60 * 60 * 24);
      return daysSinceAsked > 7;
    }
    
    return true;
  }

  // Show browser notification
  async showNotification(title, options = {}) {
    
    if (!this.isSupported()) {
      return false;
    }

    // Always check fresh permission status
    this.permission = Notification.permission;
    
    if (this.permission !== 'granted') {
      // Try to request permission if it's default
      if (this.permission === 'default') {
        const newPermission = await this.requestPermission();
        if (newPermission !== 'granted') {
          return false;
        }
      } else {
        return false;
      }
    }

    try {
      const defaultOptions = {
        icon: 'https://via.placeholder.com/128/000000/FFFFFF?text=SR', // SecureRoom icon
        badge: 'https://via.placeholder.com/64/000000/FFFFFF?text=S',
        tag: `secureroom-${Date.now()}`, // Unique tag for each notification
        requireInteraction: true, // Keep notification visible like WhatsApp
        silent: false,
        vibrate: [200, 100, 200],
        renotify: true, // Important for showing multiple notifications
        timestamp: Date.now(),
        data: {
          dateOfArrival: Date.now(),
          primaryKey: 1
        },
        actions: options.actions || [],
        ...options
      };

      
      // Use service worker if available for more reliable notifications
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const registration = await navigator.serviceWorker.ready;
        const notification = await registration.showNotification(title, defaultOptions);
        return notification;
      } else {
        // Fallback to regular Notification API
        const notification = new Notification(title, defaultOptions);

        // Play notification sound like WhatsApp
        this.playNotificationSound();

        // Handle notification events
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          notification.close();
          
          // Handle custom click action if provided
          if (options.onClick) {
            options.onClick(event);
          }
        };

        notification.onclose = () => {
          if (options.onClose) {
            options.onClose();
          }
        };

        notification.onerror = (error) => {
          if (options.onError) {
            options.onError(error);
          }
        };

        notification.onshow = () => {
        };

        // Auto close after 10 seconds if not require interaction
        if (!defaultOptions.requireInteraction) {
          setTimeout(() => {
            notification.close();
          }, 10000);
        }

        // Return the notification object
        return notification;
      }
    } catch (error) {
      return false;
    }
  }

  // Show notification with type-based styling
  showTypedNotification(type, title, message, options = {}) {
    
    const types = {
      success: {
        icon: '✅',
        badge: '/favicon.ico',
        vibrate: [200, 100, 200]
      },
      error: {
        icon: '❌',
        badge: '/favicon.ico',
        vibrate: [500, 250, 500]
      },
      warning: {
        icon: '⚠️',
        badge: '/favicon.ico',
        vibrate: [300, 150, 300]
      },
      info: {
        icon: 'ℹ️',
        badge: '/favicon.ico',
        vibrate: [100, 50, 100]
      }
    };

    const typeConfig = types[type] || types.info;
    
    const finalOptions = {
      body: message,
      ...typeConfig,
      ...options
    };
    
    
    return this.showNotification(title, finalOptions);
  }

  // Clear all notifications
  clearAll() {
    if (!this.isSupported()) return;
    
    // Close all notifications with our tag
    if ('getNotifications' in ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker?.ready.then(registration => {
        registration.getNotifications({ tag: 'lockbox-notification' })
          .then(notifications => {
            notifications.forEach(notification => notification.close());
          });
      });
    }
  }

  // Check and initialize notification settings
  async initialize() {
    if (!this.isSupported()) {
      return false;
    }

    this.permission = Notification.permission;
    this.enabled = this.permission === 'granted';

    // Auto-request permission if appropriate
    if (this.permission === 'default' && this.shouldAskPermission()) {
      // We'll show a user-friendly prompt instead of immediately requesting
    }

    return this.enabled;
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;